import { Tag, Typography } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import { BRAND_COLORS } from '../../../constants/styles';
import { DonationRecord } from '../../../types/phlebotomist-dashboard';

const { Text } = Typography;

// Status colors using brand palette
const STATUS_COLORS: Record<DonationRecord['status'], string> = {
  Complete: BRAND_COLORS.secondary.warmBeige, // #4f6d7a - Warm beige for complete
  'DBL→SGL': BRAND_COLORS.primary.deepNavy, // #0e4d8f - Deep navy for conversion
  Ineligible: BRAND_COLORS.primary.red, // #f31a25 - Brand red for ineligible
  Partial: BRAND_COLORS.primary.royalBlue, // #1565c0 - Royal blue for partial
  Failed: '#ff4d4f', // Red for failed (zero yield)
  Pending: '#faad14', // Amber for not yet processed
};

// Table-specific colors from brand palette
const TABLE_COLORS = {
  type: {
    double: BRAND_COLORS.primary.red, // #f31a25
    single: BRAND_COLORS.primary.royalBlue, // #1565c0
  },
  pRBC: {
    expectedMatch: BRAND_COLORS.secondary.softTaupe, // #dce0e4
    expectedNoMatch: BRAND_COLORS.secondary.lightGray, // #a7b4c2
    actualMatch: BRAND_COLORS.secondary.warmBeige, // #4f6d7a
    actualNoMatch: BRAND_COLORS.secondary.coolMint, // #6e7a8a
  },
  FFP: {
    expectedDouble: BRAND_COLORS.secondary.lightSage, // #cedbef
    expectedSingle: BRAND_COLORS.secondary.slateGray, // #2f3a45
    actualDouble: BRAND_COLORS.secondary.steelBlue, // #0b345f
    actualSingle: BRAND_COLORS.secondary.cloud, // #e8ecef
  },
};

interface IGetColumnsOptions {
  showPhlebotomist?: boolean;
  isMobile?: boolean;
  donations?: DonationRecord[];
}

const formatPhlebotomistName = (name: string, isMobile: boolean): string => {
  if (!isMobile) return name;
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  const firstName = parts[0];
  const lastName = parts[parts.length - 1];
  return `${firstName.charAt(0)}. ${lastName}`;
};

export const getColumns = (
  t: TFunction,
  options: IGetColumnsOptions = {}
): ColumnsType<DonationRecord> => {
  const {
    showPhlebotomist = false,
    isMobile = false,
    donations = [],
  } = options;

  // Build dynamic filter values from the data
  const uniqueLocations = [
    ...new Set(donations.map((d) => d.locationName).filter(Boolean)),
  ].sort();
  const uniqueRegions = [
    ...new Set(donations.map((d) => d.regionName).filter(Boolean)),
  ].sort();
  const uniqueDonors = [
    ...new Set(donations.map((d) => d.donorName).filter(Boolean)),
  ].sort();
  const uniquePhlebotomists = [
    ...new Set(donations.map((d) => d.phlebotomistName).filter(Boolean)),
  ].sort();
  const uniqueFailureReasons = [
    ...new Set(
      donations.flatMap((d) => d.failureReasons || []).filter(Boolean)
    ),
  ].sort();

  const failureReasonLabels: Record<string, string> = {
    Clots: 'Clots',
    LowVolume: 'Low Volume',
    Hemolyzed: 'Hemolyzed',
    SealIssue: 'Seal Issue',
    BrokenBag: 'Broken Bag',
    RedCellContamination: 'Red Cell Contamination',
    DonorHealthIssue: 'Donor Health Issue',
    Lipemic: 'Lipemic',
    Other: 'Other',
    Timeout: 'Unsuccessful',
    Expired: 'Expired',
  };

  const columns: ColumnsType<DonationRecord> = [
    {
      title: t('table.columns.date', 'Date'),
      dataIndex: 'date',
      key: 'date',
      width: isMobile ? 80 : 110,
      render: (date: Date) => {
        const d = dayjs(date);
        return (
          <div>
            <div>{d.format(isMobile ? 'MM/DD/YY' : 'MM/DD/YYYY')}</div>
            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
              {d.format('h:mm A')}
            </div>
          </div>
        );
      },
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: t('table.columns.donor', 'Donor'),
      dataIndex: 'donorName',
      key: 'donorName',
      width: 60,
      sorter: (a, b) => a.donorName.localeCompare(b.donorName),
      filters: uniqueDonors.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => record.donorName === value,
    },
    {
      title: t('table.columns.location', 'Location'),
      dataIndex: 'locationName',
      key: 'locationName',
      width: 140,
      sorter: (a, b) => a.locationName.localeCompare(b.locationName),
      filters: uniqueLocations.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => record.locationName === value,
    },
    {
      title: t('table.columns.region', 'Region'),
      dataIndex: 'regionName',
      key: 'regionName',
      width: 120,
      sorter: (a, b) => a.regionName.localeCompare(b.regionName),
      filters: uniqueRegions.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => record.regionName === value,
    },
    {
      title: t('table.columns.type', 'Type'),
      dataIndex: 'type',
      key: 'type',
      width: isMobile ? 50 : 60,
      render: (type: 'Single' | 'Double') => (
        <Tag
          color={
            type === 'Double'
              ? TABLE_COLORS.type.double
              : TABLE_COLORS.type.single
          }
        >
          {isMobile ? (type === 'Double' ? 'DBL' : 'SGL') : type}
        </Tag>
      ),
      sorter: (a, b) => a.type.localeCompare(b.type),
      filters: [
        { text: 'Single', value: 'Single' },
        { text: 'Double', value: 'Double' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: t('table.columns.approvedML', 'Approved ML'),
      dataIndex: 'approvedML',
      key: 'approvedML',
      width: 110,
      render: (ml: number) => (ml > 0 ? `${ml}ml` : '-'),
      sorter: (a, b) => a.approvedML - b.approvedML,
    },
    {
      title: t('table.columns.expectedPRBC', 'Exp pRBC'),
      key: 'expectedPRBC',
      width: 16,
      align: 'center' as const,
      sorter: (a, b) => (a.expectedPRBC || 0) - (b.expectedPRBC || 0),
      render: (_: unknown, record: DonationRecord) => {
        if (!record.expectedPRBC) return '-';
        const matches = record.expectedPRBC === record.actualPRBC;
        return (
          <Text
            strong
            style={{
              color: matches
                ? TABLE_COLORS.pRBC.expectedMatch
                : TABLE_COLORS.pRBC.expectedNoMatch,
            }}
          >
            {record.expectedPRBC}
          </Text>
        );
      },
    },
    {
      title: t('table.columns.actualPRBC', 'Act pRBC'),
      key: 'actualPRBC',
      width: 16,
      align: 'center' as const,
      sorter: (a, b) => (a.actualPRBC || 0) - (b.actualPRBC || 0),
      render: (_: unknown, record: DonationRecord) => {
        if (!record.actualPRBC) return '-';
        const matches = record.expectedPRBC === record.actualPRBC;
        return (
          <Text
            strong
            style={{
              color: matches
                ? TABLE_COLORS.pRBC.actualMatch
                : TABLE_COLORS.pRBC.actualNoMatch,
            }}
          >
            {record.actualPRBC}
          </Text>
        );
      },
    },
    {
      title: t('table.columns.expectedFFP', 'Exp FFP'),
      key: 'expectedFFP',
      width: 80,
      align: 'center' as const,
      sorter: (a, b) =>
        (a.expectedFFPDouble || a.expectedFFPSingle || 0) -
        (b.expectedFFPDouble || b.expectedFFPSingle || 0),
      render: (_: unknown, record: DonationRecord) => {
        if (record.expectedFFPDouble) {
          return (
            <Tag color={TABLE_COLORS.FFP.expectedDouble} style={{ margin: 0 }}>
              {record.expectedFFPDouble} DBL
            </Tag>
          );
        }
        if (record.expectedFFPSingle) {
          return (
            <Tag color={TABLE_COLORS.FFP.expectedSingle} style={{ margin: 0 }}>
              {record.expectedFFPSingle} SGL
            </Tag>
          );
        }
        return '-';
      },
    },
    {
      title: t('table.columns.actualFFP', 'Act FFP'),
      key: 'actualFFP',
      width: 80,
      align: 'center' as const,
      sorter: (a, b) =>
        (a.actualFFPDouble || a.actualFFPSingle || 0) -
        (b.actualFFPDouble || b.actualFFPSingle || 0),
      render: (_: unknown, record: DonationRecord) => {
        if (record.actualFFPDouble) {
          return (
            <Tag color={TABLE_COLORS.FFP.actualDouble} style={{ margin: 0 }}>
              {record.actualFFPDouble} DBL
            </Tag>
          );
        }
        if (record.actualFFPSingle) {
          return (
            <Tag color={TABLE_COLORS.FFP.actualSingle} style={{ margin: 0 }}>
              {record.actualFFPSingle} SGL
            </Tag>
          );
        }
        return '-';
      },
    },
    {
      title: t('table.columns.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 75,
      render: (status: DonationRecord['status']) => (
        <Tag color={STATUS_COLORS[status]}>{status}</Tag>
      ),
      sorter: (a, b) => a.status.localeCompare(b.status),
      filters: [
        { text: 'Complete', value: 'Complete' },
        { text: 'DBL→SGL', value: 'DBL→SGL' },
        { text: 'Partial', value: 'Partial' },
        { text: 'Failed', value: 'Failed' },
        { text: 'Pending', value: 'Pending' },
        { text: 'Ineligible', value: 'Ineligible' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: t('table.columns.failureReasons', 'Failure Reason'),
      dataIndex: 'failureReasons',
      key: 'failureReasons',
      width: 140,
      filters: uniqueFailureReasons.map((r) => ({
        text: failureReasonLabels[r] || r,
        value: r,
      })),
      onFilter: (value, record) =>
        (record.failureReasons || []).includes(value as string),
      sorter: (a, b) => {
        const aStr = (a.failureReasons || []).join(',');
        const bStr = (b.failureReasons || []).join(',');
        return aStr.localeCompare(bStr);
      },
      render: (reasons: string[]) => {
        if (!reasons || reasons.length === 0) return '-';
        return reasons.map((r) => failureReasonLabels[r] || r).join(', ');
      },
    },
  ];

  // Add Phlebotomist column if showPhlebotomist is true (for Admin/MD users)
  if (showPhlebotomist) {
    // Insert after Date column (index 0), so it becomes column #2
    columns.splice(1, 0, {
      title: t('table.columns.phlebotomist', 'Phlebotomist'),
      dataIndex: 'phlebotomistName',
      key: 'phlebotomistName',
      width: isMobile ? 70 : 140,
      sorter: (a, b) => a.phlebotomistName.localeCompare(b.phlebotomistName),
      filters: uniquePhlebotomists.map((name) => ({ text: name, value: name })),
      onFilter: (value, record) => record.phlebotomistName === value,
      render: (name: string) => formatPhlebotomistName(name, isMobile),
    });
  }

  // Hide certain columns on mobile (<992px)
  if (isMobile) {
    const hiddenKeys = ['locationName', 'regionName', 'approvedML'];
    return columns.filter((col) => !hiddenKeys.includes(col.key as string));
  }

  return columns;
};
